/**
 * Helpers for format-insensitive search on serial numbers (client-side mirror
 * of server/utils/searchNormalize.js).
 *
 * Serial numbers are written in several ways for the same device: "23-3142",
 * "233142" and "NAV-233142" (IGC certified devices carry the "NAV-" prefix,
 * others do not). Users often do not know which spelling is stored, so search
 * must ignore the punctuation.
 *
 * The rule: strip every character that is not a letter or a digit, on both
 * sides of the comparison. Letters are kept, so "NAV" is never dropped.
 */

/**
 * Strip non-alphanumeric characters and lowercase, for case-insensitive
 * substring matching.
 *
 * @param {*} value - The raw term or stored value.
 * @returns {string} The normalized value, or '' when there is nothing left.
 */
export function normalizeSearchTerm(value) {
  if (typeof value !== 'string') return '';
  return value.replace(/[^A-Za-z0-9]/g, '').toLowerCase();
}

/**
 * Test whether a serial number matches a typed term, ignoring formatting.
 * Falls back to a plain substring match so a term made only of punctuation
 * still behaves predictably.
 *
 * @param {*} serial - The stored serial number.
 * @param {*} term - What the user typed.
 * @returns {boolean}
 */
export function serialMatchesSearch(serial, term) {
  if (typeof serial !== 'string' || typeof term !== 'string') return false;
  if (serial.toLowerCase().includes(term.trim().toLowerCase())) return true;
  const normalizedTerm = normalizeSearchTerm(term);
  if (!normalizedTerm) return false;
  return normalizeSearchTerm(serial).includes(normalizedTerm);
}
